import { idb } from "../../db/index.ts";
import { g, local } from "../../util/index.ts";
import isUntradable from "./isUntradable.ts";
import makeItWork from "./makeItWork.ts";
import processTrade from "./processTrade.ts";
import summary from "./summary.ts";
import type { TradeTeams } from "../../../common/types.ts";
import { isSport } from "../../../common/sportFunctions.ts";
import { choice, rand } from "../../../common/random.ts";
import { ValueChangeCalculator } from "../team/ValueChangeCalculator.ts";
import { team } from "../index.ts";
import { apronsActive, isOverSecondApron } from "../finances/aprons.ts";

// Apron-driven salary shedding: a REBUILDING team stuck over the second apron will trade a
// good/expensive player at slightly below market to escape the apron -- it can't justify the
// tax/roster straitjacket while it isn't contending. A CONTENDING team over the apron (e.g. a
// title window worth paying for) is intentionally left alone, so it keeps its stars. Soft, not
// a hard rule: it just biases who shops whom, and how much value the shedder will eat.
const SHED_ATTEMPT_PROB = 0.5; // of AI trade attempts, target an over-apron rebuilder this often

// Normally a team walks away from a trade whose value swing (dv) is worse than -15. A qualifying
// apron dump (one that actually cuts the shedder's payroll while it's over the 2nd apron) may go
// this far below market instead, valuing the cap relief. Tuned to "slightly below market", not
// "give players away".
const SHED_DV_FLOOR = 30;

// Teams manually held out of apron shedding for now. OKC is a genuine second-apron CONTENDER
// this season and should keep its roster regardless of how the strategy signal happens to read,
// so exclude it explicitly (belt-and-suspenders on top of the "rebuilding" gate below).
const SHED_EXCLUDE_ABBREVS = new Set(["OKC"]);

// A player parked on a last-resort "prove-it" deal is unhappy and angling for a way out (see
// core/freeAgents/proveIt.ts). Bias AI trade attempts toward moving such players: the holding team
// shops him, mirroring a real disgruntled-player trade request. Independent of (and checked after)
// apron shedding.
const DISGRUNTLED_ATTEMPT_PROB = 0.4;

// Map of tid -> pids of that team's currently-tradeable prove-it players.
const getDisgruntledPids = async (): Promise<Map<number, number[]>> => {
	const map = new Map<number, number[]>();
	for (const t of await idb.cache.teams.getAll()) {
		if (t.disabled) {
			continue;
		}
		const roster = await idb.cache.players.indexGetAll("playersByTid", t.tid);
		const pids = roster
			.filter((p) => p.contract.proveIt && !isUntradable(p).untradable)
			.map((p) => p.pid);
		if (pids.length > 0) {
			map.set(t.tid, pids);
		}
	}
	return map;
};

// AI teams that are over the second apron AND rebuilding -> motivated to shed salary.
const getShedTids = async (): Promise<Set<number>> => {
	const set = new Set<number>();
	if (!apronsActive()) {
		return set;
	}
	for (const t of await idb.cache.teams.getAll()) {
		if (
			t.disabled ||
			t.strategy !== "rebuilding" ||
			SHED_EXCLUDE_ABBREVS.has(t.abbrev)
		) {
			continue;
		}
		if (isOverSecondApron(await team.getPayroll(t.tid))) {
			set.add(t.tid);
		}
	}
	return set;
};

const getAITids = async () => {
	const teams = await idb.cache.teams.getAll();
	return teams
		.filter((t) => {
			if (t.disabled) {
				return false;
			}

			if (
				(local.autoPlayUntil || g.get("spectator")) &&
				!g.get("challengeNoTrades")
			) {
				return true;
			}
			return !g.get("userTids").includes(t.tid);
		})
		.map((t) => t.tid);
};

const attempt = async (
	valueChangeCalculator: ValueChangeCalculator,
	shedTids: Set<number>,
	disgruntledPids: Map<number, number[]>,
) => {
	const aiTids = await getAITids();

	if (aiTids.length === 0) {
		return false;
	}

	// Bias the initiating team toward an over-apron rebuilder looking to shed salary; failing that,
	// toward a team holding a disgruntled prove-it player it wants to move.
	const shedInitiatorTids = aiTids.filter((t) => shedTids.has(t));
	const shedding =
		shedInitiatorTids.length > 0 && rand() < SHED_ATTEMPT_PROB;

	const disgruntledInitiatorTids = aiTids.filter((t) =>
		disgruntledPids.has(t),
	);
	const forcingOut =
		!shedding &&
		disgruntledInitiatorTids.length > 0 &&
		rand() < DISGRUNTLED_ATTEMPT_PROB;

	const tid = shedding
		? choice(shedInitiatorTids)
		: forcingOut
			? choice(disgruntledInitiatorTids)
			: choice(aiTids);
	const otherTids = aiTids.filter((tid2) => tid !== tid2);

	if (otherTids.length === 0) {
		return false;
	}

	const otherTid = choice(otherTids);
	const players = (
		await idb.cache.players.indexGetAll("playersByTid", tid)
	).filter((p) => !isUntradable(p).untradable);
	const draftPicks = await idb.cache.draftPicks.indexGetAll(
		"draftPicksByTid",
		tid,
	);

	if (players.length === 0 && draftPicks.length === 0) {
		return false;
	}

	const r = rand();
	const pids: number[] = [];
	const dpids: number[] = [];

	// When shedding salary, weight by contract size (shop the biggest contracts) rather than
	// value, and always move a player -- shedding a pick frees no payroll.
	const playerWeight = shedding
		? (p: { contract: { amount: number } }) => p.contract.amount
		: (p: { value: number }) => p.value;

	// When forcing out a disgruntled prove-it player, that player IS the asset being shopped.
	const forcingOutPids = forcingOut
		? (disgruntledPids.get(tid) ?? []).filter((pid) =>
				players.some((p) => p.pid === pid),
			)
		: [];

	if (forcingOutPids.length > 0) {
		pids.push(choice(forcingOutPids));
	} else if (
		(shedding || r < 0.7 || draftPicks.length === 0) &&
		players.length > 0
	) {
		// Weight by player value - good player more likely to be in trade
		const p = choice(players, playerWeight);
		if (!p) {
			return false;
		}
		pids.push(p.pid);
	} else if ((r < 0.85 || players.length === 0) && draftPicks.length > 0) {
		dpids.push(choice(draftPicks).dpid);
	} else {
		// Weight by player value - good player more likely to be in trade
		const p = choice(players, playerWeight);
		const dp = choice(draftPicks);
		if (!p || !dp) {
			return false;
		}
		pids.push(p.pid);
		dpids.push(dp.dpid);
	}

	const teams0: TradeTeams = [
		{
			dpids,
			dpidsExcluded: [],
			pids,
			pidsExcluded: [],
			tid,
		},
		{
			dpids: [],
			dpidsExcluded: [],
			pids: [],
			pidsExcluded: [],
			tid: otherTid,
		},
	];

	const teams = await makeItWork(teams0, {
		holdUserConstant: false,
		maxAssetsToAdd: 5,
		valueChangeCalculator,
	});

	if (!teams) {
		return false;
	}

	// Don't do trades of just picks, it's weird usually
	if (teams[0].pids.length === 0 && teams[1].pids.length === 0) {
		return false;
	}

	// Don't do trades for nothing, it's weird usually
	if (teams[1].pids.length === 0 && teams[1].dpids.length === 0) {
		return false;
	}

	const tradeSummary = await summary(teams);

	if (tradeSummary.warning) {
		return false;
	}

	// Make sure this isn't a really shitty trade. A qualifying apron dump -- an over-2nd-apron
	// shedder actually cutting its payroll -- is allowed to go further below market than normal,
	// valuing the cap relief over the lost value. (summary payrolls are in $M; apron helpers work
	// in thousands.)
	const before = tradeSummary.teams[0].payrollBeforeTrade * 1000;
	const after = tradeSummary.teams[0].payrollAfterTrade * 1000;
	const isApronDump =
		shedding && isOverSecondApron(before) && after < before;
	const dvFloor = isApronDump ? SHED_DV_FLOOR : 15;

	const dv2 = await valueChangeCalculator.evaluate({
		tid: teams[0].tid,
		pidsAdd: teams[1].pids,
		pidsRemove: teams[0].pids,
		dpidsAdd: teams[1].dpids,
		dpidsRemove: teams[0].dpids,
		tradingPartnerTid: undefined,
	});
	if (dv2 > 15 || dv2 < -dvFloor) {
		return false;
	}

	const finalTids: [number, number] = [teams[0].tid, teams[1].tid];
	const finalPids: [number[], number[]] = [teams[0].pids, teams[1].pids];
	const finalDpids: [number[], number[]] = [teams[0].dpids, teams[1].dpids];
	await processTrade(finalTids, finalPids, finalDpids);

	return finalTids;
};

const DEFAULT_NUM_TEAMS = 30;

const betweenAiTeams = async () => {
	if (g.get("forceHistoricalRosters")) {
		return false;
	}

	// If aiTradesFactor is not an integer, use the fractional part as a probability. Like for 3.5, 50% of the times it will be 3, and 50% will be 4.
	// Also scale so there are fewer trade attempts if there are fewer teams.
	let float = g.get("aiTradesFactor");
	if (isSport("baseball")) {
		float *= 0.25;
	}
	if (g.get("numActiveTeams") < DEFAULT_NUM_TEAMS) {
		float *= g.get("numActiveTeams") / DEFAULT_NUM_TEAMS;
	}
	let numAttempts = Math.floor(float);
	const remainder = float % 1;
	if (remainder > 0 && rand() < remainder) {
		numAttempts += 1;
	}

	if (numAttempts > 0) {
		const valueChangeCalculator = new ValueChangeCalculator();
		const shedTids = await getShedTids();
		const disgruntledPids = await getDisgruntledPids();

		for (let i = 0; i < numAttempts; i++) {
			const tradeTids = await attempt(
				valueChangeCalculator,
				shedTids,
				disgruntledPids,
			);
			if (tradeTids) {
				// Don't need to recompute draft pick value
				valueChangeCalculator.invalidateCache({ teams: tradeTids });
			}
		}
	}
};

export default betweenAiTeams;
